/* ============================================================================
 * AE Foodstore — Mini-Cart Popup behavior (Build #3 — 2026-05-17)
 * Spec: .cowork-context/project_mini_cart_weee_spec.md
 *
 * Triggers (no fetch/XHR patching — pure observer + event-driven):
 *   - MutationObserver on #Cartcount (bulletproof — cart-card-indicator.js
 *     updates badge after every add path; mutation fires regardless of how
 *     /cart/add was invoked: form POST, jQuery $.ajax, fetch, Shopify Buy SDK)
 *   - `cart:updated` event (dispatched by wbcartdropdown.js + cart-notification.js)
 *   - `cart:item-added` / `product:added-to-cart` / `cart:added` events
 *
 *   Auto-close 4500ms after add (canceled if user hovers popup)
 *   Hover cart icon (desktop) → persistent, mouseleave + 200ms grace → close
 *   Click cart icon → navigate /cart (desktop) / toggle bottom-sheet (mobile)
 *   ESC / click-outside / backdrop tap / mobile swipe-down → close
 *
 * Build #3 changes:
 *   - BUG-D fix: syncCountAndState now also updates #CartTotalText (header
 *     visible cart total) — previously only popup elements updated, header
 *     price stayed stale until page reload.
 *   - BUG-E/F defensive fix: removed window.fetch + XMLHttpRequest.prototype
 *     patches. With Honey extension installed, prototype patches stacked with
 *     extension patches and may have interfered with product-form.js. The
 *     MutationObserver on #Cartcount is sufficient and cannot interfere with
 *     any theme code path.
 *   - P3 sort fix: removed .reverse() — /cart.js in this theme returns items
 *     in latest-first order natively; the previous reverse flipped them to
 *     oldest-top.
 *
 * Rows render: fetched from /cart.js JSON and built client-side.
 * ========================================================================== */
/* ============================================================================
 * P0 HOTFIX (build #5, 2026-05-17) — Stub <cart-notification> custom element.
 *
 * Root cause of BUG-E/F (silent Add To Cart): assets/cart-notification.js is
 * not loaded as a <script> anywhere in the theme, so the <cart-notification>
 * element (rendered by snippets/cart-notification.liquid) is an unknown
 * HTMLElement with no custom methods. product-form.js's onSubmitHandler does:
 *
 *   this.cartNotification = document.querySelector("cart-notification");
 *   ...
 *   this.cartNotification.setActiveElement(document.activeElement);
 *   formData.append("sections", this.cartNotification.getSectionsToRender()...);
 *
 * Both calls throw "not a function" → onSubmitHandler aborts silently → cart
 * never updates → popup never shows. Defining a stub class with the required
 * method surface lets product-form.js proceed; mini-cart-popup keeps its own
 * auto-refresh via MutationObserver on #Cartcount (build #3+) so the popup
 * shows on add via the existing path. renderContents() also dispatches
 * `cart:updated` so listeners (free-shipping-bar, our refresh) see the change.
 *
 * Idempotent: only defines if `cart-notification` isn't already registered.
 * Defined at file load (not inside init/IIFE) so it's available the moment
 * product-form.js constructor runs (mini-cart-popup.js loads before
 * product-form.js per defer document order: header section → layout body).
 * ========================================================================== */
if (window.customElements && !window.customElements.get('cart-notification')) {
  try {
    class AefCartNotificationStub extends HTMLElement {
      setActiveElement(el) {
        this._lastTriggerEl = el;
      }
      getSectionsToRender() {
        /* 15/9/2026 (vòng 49) — ĐÃ GỠ { id: 'cart-icon-bubble' } KHỎI DANH SÁCH NÀY.
         *
         * product-form.js:44 nhét mảng này vào `formData.append("sections", …)`
         * của POST /cart/add.js, nên mỗi tên ở đây là một MỆNH LỆNH cho máy chủ
         * Shopify: "dựng section đó ra rồi gửi kèm". Đo trên LIVE 15/9, giỏ TRỐNG:
         *
         *   ?sections=cart-notification-product,cart-notification-button
         *        →   251 byte
         *   ?sections=cart-notification-product,cart-notification-button,cart-icon-bubble
         *        → 5.324 byte
         *
         * 5.073 byte thừa MỖI LƯỢT thêm vào giỏ, và còn phồng theo số dòng trong
         * giỏ vì sections/cart-icon-bubble.liquid lặp qua `cart.items`. Toàn bộ
         * chỗ đó bị vứt: renderContents() ngay dưới đây không đọc `parsedState`,
         * nó chỉ bắn `cart:updated`. assets/cart.js đã gỡ đúng mục này từ 16/8
         * (xem chú thích cart.js:294) — đường song song ở đây bị bỏ quên.
         *
         * Section đó nay đã bị XOÁ khỏi kho luôn: không một {% section %}, không
         * một templates/*.json, không một sections/*-group.json, không một dòng
         * settings_data.json nào gọi nó, và nó không có {% schema %} nên theme
         * editor cũng không thêm được. Nhưng markup nó dựng ra mang
         * `id="toggleCountCart"` — ĐÚNG id thẻ <a> giỏ của sections/header.liquid,
         * thứ mà chính tệp này gọi getElementById lên.
         *
         * Người gác: scripts/test-section-gio-chet-khong-con-ngoi.js */
        return [
          { id: 'cart-notification-product' },
          { id: 'cart-notification-button' }
        ];
      }
      renderContents(parsedState) {
        // Mini-cart-popup auto-refreshes via MutationObserver on #Cartcount +
        // cart:updated event listeners. Dispatch the event so they fire, even
        // if cart-card-indicator.js was the one that updated the badge.
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { source: 'product-form', cart: parsedState || null }
          }));
        } catch (e) {
          var evt = document.createEvent('Event');
          evt.initEvent('cart:updated', true, true);
          document.dispatchEvent(evt);
        }
      }
      open() {}
      close() {}
    }
    window.customElements.define('cart-notification', AefCartNotificationStub);
  } catch (defineErr) {
    if (window.console && console.warn) {
      console.warn('[aef-mini-cart] cart-notification stub define failed:', defineErr);
    }
  }
}

(function () {
  'use strict';
  if (window.__aefMiniCartBound) return;
  window.__aefMiniCartBound = true;

  var MOBILE_BP = 768;
  var HOVER_GRACE_MS = 200;
  var AUTO_CLOSE_MS = 4500;

  var bubble, btn, popup, card, backdrop, closeBtn, countEl, listContainer;
  var hoverTimer = null;
  var autoCloseTimer = null;
  var isOpen = false;
  var lastFocused = null;
  var lastCount = 0;
  var refreshInflight = false;
  var refreshPending = false;
  var pendingShowAdd = false;

  /* ═══ 14/9/2026 (vòng 48) — CỜ TỰ-GHI CHO HUY HIỆU #Cartcount ═══════════════
   *
   * MutationObserver ở :~470 dựng lên để nghe NGƯỜI LẠ ghi huy hiệu
   * (cart-card-indicator.js / wbcartdropdown.js / cart-notification.js /
   * perf-instant.js) — chú thích khai sinh nó nói rõ như vậy. Nhưng từ vòng 43,
   * chính tệp này cũng ghi huy hiệu (lượt ghi LẠC QUAN lúc bấm, :~845), nên
   * observer nghe luôn ngòi bút của nhà mình rồi đi hỏi /cart.js — mà lúc đó
   * máy chủ CHƯA cộng hàng ⇒ câu trả lời chưa-có-hàng ghi đè ngược.
   *
   * ĐO ĐƯỢC (jsdom chạy nguyên văn tệp LIVE, /cart.js 70ms · /cart/add.js 320ms,
   * 1 cú bấm, giỏ rỗng): 98ms huy hiệu="1" → 174ms huy hiệu="0" → 429ms mới về
   * "1". Mà `.aef-cart-badge[data-count="0"]{display:none !important}`
   * (ae-delivery-widget.css:685-688) ⇒ chấm đỏ BIẾN MẤT HẲN ~255ms rồi hiện lại.
   * Tràng 5 cú trên 5 thẻ khác nhau: mất 3 lần, và 13 lượt /cart.js cho 5 cú bấm.
   *
   * CHỮA: đánh dấu lượt ghi của CHÍNH MÌNH, observer bỏ qua đúng lượt đó — trả
   * observer về đúng vai trò nó sinh ra. KHÔNG chọn cách "chốt __aefCartAddPending
   * trong refreshFromServer": cách đó bật `refreshPending` mà không ai rút (chỗ
   * rút duy nhất là `.then()` cuối của một lượt fetch ĐANG BAY, :~355), nên số
   * cuối chỉ đúng nhờ một tệp KHÁC (cart-card-indicator.js:55) tình cờ ghi lại.
   *
   * VÌ SAO KHÔNG MẤT `data-count`: observer là nơi DUY NHẤT gương data-count cho
   * 4 người ghi kia, nhưng lượt ghi của chính mình đã tự đặt data-count ngay tại
   * chỗ ghi (:~290), nên bỏ qua ở observer không để lại lỗ nào.
   *
   * VÌ SAO HẠ CỜ Ở CUỐI CALLBACK chứ không `finally` ngay sau lượt ghi: observer
   * giao bản ghi ở ĐIỂM CHỐT MICROTASK, tức SAU khi hàm ghi chạy xong — hạ cờ
   * trong `finally` là hạ trước khi observer kịp đọc, cờ thành vô dụng. Một lượt
   * giao gom HẾT bản ghi đang xếp hàng, nên hạ về 0 ở cuối callback là đủ và
   * không để cờ kẹt lại. ═══════════════════════════════════════════════════ */
  var tuGhiHuyHieu = 0;    // >0 = có lượt ghi huy hiệu của CHÍNH tệp này chưa được observer đọc
  var tuGhiGiaTri = null;  // chuỗi số vừa tự ghi — observer phải khớp mới được bỏ qua

  function isMobile() { return window.matchMedia('(max-width: ' + MOBILE_BP + 'px)').matches; }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function sizedImage(url, w) {
    if (!url) return '';
    // Inject _96x suffix before extension for Shopify CDN URLs.
    var clean = url.replace(
      /_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master)\.(jpe?g|png|webp|gif)/i,
      function (_m, ext) { return '_' + w + 'x.' + ext; }
    );
    if (clean === url) {
      clean = url.replace(/\.(jpe?g|png|webp|gif)(\?|$)/i, '_' + w + 'x.$1$2');
    }
    return clean;
  }

  /* 23/10/2026 (GG1) — tiền đi qua MỘT hàm window.aefTien (layout/theme.liquid, <head>),
     cùng định dạng filter money của Liquid. Bản cũ in "$1019.32" vào tổng header 🖥 ngay
     cạnh "$1,019.32" của /cart. Dự phòng cũng có dấu phẩy. Người gác:
     scripts/test-tien-mot-dinh-dang.js */
  function money(cents) {
    if (cents == null) return '';
    if (typeof window.aefTien === 'function') return window.aefTien(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ─── POPUP NÀY ĐÃ BỊ THAY, NHƯNG TÁC DỤNG PHỤ THÌ CHƯA (18/8/2026) ───────
     assets/aef-cart-drawer.css:8 đặt `.aef-mini-cart { display: none !important }`
     — nguyên văn chú thích ở đó: "hide the legacy Weee popover — the drawer is
     the cart UI now". Luật nằm trong aef-head-bundle.css nên áp cho MỌI trang,
     mọi bề ngang, và có !important nên luật không-important ở mini-cart-popup.css
     không cãi lại được.

     Nhưng file JS này không biết chuyện đó. show() vẫn chạy đủ bài, và trên 📱
     nó gắn `body.aef-mini-cart-open` = `overflow: hidden`, rồi chỉ gỡ ra sau
     AUTO_CLOSE_MS = 4500 ms.

     ⇒ Khách bấm "+" trên điện thoại là TRANG KHOÁ CUỘN 4,5 GIÂY, và không có
     gì hiện ra để giải thích. Tiệm tạp hoá khách thêm 10–20 món một lượt, nên
     đây là 4,5 giây × 15 lần trong một phiên mua.

     HỎI trình duyệt xem nó có thật sự vẽ ra không, đừng đoán theo tên class.
     Kiểm SAU khi gắn .is-open chứ không phải trước: nếu mai có ai viết luật
     chỉ hiện khi có .is-open thì kiểm trước sẽ khoá oan popup vĩnh viễn.
     Không đo được (môi trường lạ) thì trả true = chạy như cũ, không đoán bừa. */
  function dangVeRaKhong() {
    try {
      var w = (popup.ownerDocument && popup.ownerDocument.defaultView) || window;
      return w.getComputedStyle(popup).display !== 'none';
    } catch (e) { return true; }
  }

  function show(reason) {
    if (!popup) return;
    clearAutoClose();
    if (!isOpen) lastFocused = document.activeElement;
    popup.classList.add('is-open');
    if (!dangVeRaKhong()) {
      /* Bị CSS khác ẩn hẳn → không diễn gì, và TUYỆT ĐỐI không khoá cuộn.
         Trả nguyên trạng để không để lại class mồ côi trên DOM. */
      popup.classList.remove('is-open');
      return;
    }
    popup.setAttribute('aria-hidden', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    // Build #4 mobile polish: bottom-sheet is a true modal on mobile (covers
    // the page with a backdrop, body-scroll-locked). Flip aria-modal so
    // assistive tech treats it as such. On desktop, popup is a hover preview
    // (non-modal), so aria-modal stays false.
    if (isMobile()) {
      document.body.classList.add('aef-mini-cart-open');
      popup.setAttribute('aria-modal', 'true');
    } else {
      popup.setAttribute('aria-modal', 'false');
    }
    isOpen = true;
    if (reason === 'add') {
      autoCloseTimer = setTimeout(hide, AUTO_CLOSE_MS);
    }
  }

  function hide() {
    if (!popup || !isOpen) return;
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    popup.setAttribute('aria-modal', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('aef-mini-cart-open');
    isOpen = false;
    clearAutoClose();
    clearHover();
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function clearAutoClose() { if (autoCloseTimer) { clearTimeout(autoCloseTimer); autoCloseTimer = null; } }
  function clearHover() { if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; } }

  // BUG-E/F defensive (build #4): wrap handlers so a thrown exception from
  // anything inside (or from another listener that ran earlier and left state
  // weird, e.g. Honey extension's product-form errors) cannot kill subsequent
  // listeners or our init flow. Console.warn so issues are still discoverable.
  function safe(fn, label) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('[aef-mini-cart] ' + (label || 'handler') + ' error:', e);
        }
      }
    };
  }

  function renderRows(items) {
    if (!listContainer) return;
    if (!items || items.length === 0) {
      listContainer.innerHTML = '';
      return;
    }
    // /cart.js returns items latest-first in this theme — render in array order.
    // BUG-H: each row is an <a> so the whole tile navigates to the PDP.
    var html = '';
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var title = esc(it.product_title || it.title || '');
      var qty = parseInt(it.quantity, 10) || 1;
      var url = esc(it.url || '#');
      var imgRaw = it.image || (it.featured_image && it.featured_image.url) || '';
      var img = imgRaw ? sizedImage(imgRaw, 96) : '';
      var pid = esc(it.product_id || '');
      html += '<a href="' + url + '" class="aef-mini-cart__row" data-product-id="' + pid + '"'
        +    ' aria-label="' + title + ', quantity ' + qty + '">'
        +    '<span class="aef-mini-cart__row-img" aria-hidden="true">'
        +    (img ? '<img src="' + esc(img) + '" alt="" width="48" height="48" loading="lazy" decoding="async">' : '')
        +    '</span>'
        +    '<span class="aef-mini-cart__row-name">'
        /* 🎁 dòng quà: cùng nhãn với bản Liquid ở snippets/mini-cart-popup.liquid, nhãn đứng trước để khung 2 dòng cắt tên chứ không cắt nhãn */
        +    (it.original_price === 0
              ? '<span class="aef-mini-cart__row-qua">' + esc(window.aefTFb ? window.aefTFb('qua.nhan', 'Free gift') : 'Free gift') + '</span>' + title.replace(/^FREE GIFT - /, '')
              : title)
        +    '</span>'
        +    '<span class="aef-mini-cart__row-qty">×' + qty + '</span>'
        +    '</a>';
    }
    listContainer.innerHTML = html;
  }

  function syncCountAndState(count, total) {
    if (!popup) return;
    /* ═══ 14/9/2026 (vòng 48) — SÀN CHỐNG TỤT KHI CÒN LỜI HỨA CHƯA TRẢ ════════
     *
     * Đường thêm hàng ghi LẠC QUAN lúc bấm (:943) rồi mới đi hỏi máy chủ. Giữa
     * hai mốc đó, MỌI câu trả lời của máy chủ đều là tin CŨ: hàng đợi
     * `__aefCartAddQueue` chạy nối tiếp nên lúc cú thứ 1 trả lời, cú 2-3-4-5 đã
     * hứa xong mà máy chủ chưa nhận. Ghi thẳng số đó vào huy hiệu = kéo huy hiệu
     * lùi lại.
     *
     * ĐO ĐƯỢC (jsdom chạy nguyên văn tệp này, /cart/add.js 320ms · /cart.js 70ms,
     * 5 cú bấm trên 5 THẺ KHÁC NHAU cách nhau 180ms — nút chống-bấm-hai-lần ở
     * :1253 là WeakMap THEO NÚT nên không cản):
     *     dãy huy hiệu 1 → 2 → 1 → 2 → 1 → 2 → 3 → 2 → 3 → 2 → 4 → 3 → 5
     *     5 lượt TỤT NGƯỢC · 2 lượt ghi thấp hơn cả số máy chủ đang giữ
     * và cổng toast `d.count > lastCartCount` (aef-cart-drawer.js:1853) chỉ nổ
     * 3/5 lần ⇒ khách thêm 5 món mà chỉ thấy 3 lời xác nhận.
     *
     * LUẬT: còn lời hứa chưa trả (`__aefHuaThemHuyHieu > 0`) thì tin nào THẤP HƠN
     * cái đang hiện là tin ôi — BỎ QUA CẢ CẶP, không ghi nửa số món nửa tiền
     * (count và total là MỘT CẶP; ghi một nửa là đẻ ra cảnh "5 món / $2,00").
     * Hứa trả hết thì câu này tự tắt, nên lượt hoà giải cuối vẫn ghi đúng số máy
     * chủ, và mọi lượt GIẢM thật (xoá dòng ở trang giỏ, stepper bấm "−") không
     * bị chặn: lúc đó `__aefHuaThemHuyHieu` bằng 0.
     *
     * VÌ SAO KHÔNG DÙNG `__aefCartAddPending`: cờ đó cũng được card-stepper.js:308
     * tăng cho lượt /cart/change.js — mà lượt đó có thể làm giỏ GIẢM. Mượn nó làm
     * sàn là khoá luôn đường giảm hợp lệ. Sổ lời hứa dưới đây chỉ đếm đúng những
     * lượt mà CHÍNH tệp này đã ghi trước một con số chưa được máy chủ xác nhận.
     * ══════════════════════════════════════════════════════════════════════ */
    if (typeof count === 'number' && (window.__aefHuaThemHuyHieu || 0) > 0) {
      var badgeSan = document.getElementById('Cartcount');
      var soDangHien = badgeSan ? (parseInt((badgeSan.textContent || '0').trim(), 10) || 0) : 0;
      if (count < soDangHien) return;
    }
    if (typeof count === 'number') {
      popup.classList.toggle('is-empty', count === 0);
      // 27/9/2026 — chỉ ghi SỐ + data-aef-dem. Nhãn "item/items/món" là chữ tĩnh
      // của từ điển nằm ngay sau (snippets/aef-nhan-dem-mon.liquid), luật số ít/số
      // nhiều là MỘT luật CSS — tệp này không so `count === 1` nữa, và không ghi đè
      // nhãn (ghi textContent của cả countEl là xoá luôn hai nút nhãn).
      var soEl = countEl && countEl.querySelector('[data-aef-mini-cart-so]');
      if (soEl) {
        soEl.textContent = count;
        soEl.setAttribute('data-aef-dem', String(count));
      }
      // BUG-D fix: also keep header badge in sync (cart-card-indicator.js does
      // this too on /cart/add, but redundant safety covers paths that bypass it).
      // BUG-T (build #9, 2026-05-17): also write data-count attribute so the
      // CSS rule `.aef-cart-badge[data-count="0"] { display: none }` can hide
      // the red "0" badge when the cart is empty.
      var headerBadge = document.getElementById('Cartcount');
      if (headerBadge) {
        // 14/9/2026 (vòng 48) — dán dấu TỰ GHI trước khi chạm DOM, để observer
        // ở :~505 bỏ qua đúng lượt này. Xem khối chú thích ở :~115.
        tuGhiHuyHieu++;
        tuGhiGiaTri = String(count);
        headerBadge.textContent = count;
        headerBadge.setAttribute('data-count', String(count));
      }
    }
    if (typeof total === 'number') {
      // BUG-D fix: update the header visible cart total. wbcartdropdown.js syncs
      // this on /cart/change but NOT on /cart/add — leaving header price stale
      // until page reload. Update from every cart refresh.
      var headerTotal = document.getElementById('CartTotalText');
      if (headerTotal) {
        // 23/10/2026 (GG1) — một đường: money() → window.aefTien. Nhánh Shopify.formatMoney
        // cũ (innerHTML) là mã chết trên LIVE — đã gỡ để không còn nguồn định dạng thứ hai.
        headerTotal.textContent = money(total);
      }
    }
  }

  /* 6/9/2026 (vòng 43) — CẦU NỐI SANG KHỐI HÀNG ĐỢI THÊM-HÀNG.
   *
   * Tệp này có HAI IIFE tách biệt: khối popup (ở đây) và khối hàng đợi thêm-hàng
   * (bên dưới, quanh dòng 538). Chúng KHÔNG chung phạm vi, nên khối kia không gọi
   * thẳng `syncCountAndState` được — cầu nối duy nhất xưa nay là sự kiện
   * `cart:updated`.
   *
   * Nhưng sự kiện đó KHÔNG dùng được cho lượt ghi LẠC QUAN lúc bấm: listener ở
   * :465 gọi `syncCountAndState` xong lại gọi luôn `refreshFromServer()`, mà lúc
   * bấm thì máy chủ CHƯA cộng hàng ⇒ /cart.js trả về trạng thái trước-khi-thêm và
   * ghi đè ngược con số vừa hứa. Đó đúng là BUG-C (tổng tiền dao động $0 ↔ $2,99
   * sáu lần trong 750ms). Cộng thêm 9 nơi khác cũng nghe sự kiện này.
   *
   * Nên mở đúng MỘT lối gọi thẳng. Vẫn là MỘT NGƯỜI GHI (P1): người ghi vẫn là
   * `syncCountAndState`, chỉ thêm cách gọi tới nó mà không đánh thức 10 listener. */
  window.aefGhiSoGio = syncCountAndState;

  function refreshFromServer(triggerShowOnAdd) {
    if (refreshInflight) {
      refreshPending = true;
      if (triggerShowOnAdd) pendingShowAdd = true;
      return;
    }
    refreshInflight = true;
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // INVARIANT (bug-prevention 2026-06-04): /cart.js must return CART
        // STATE with a numeric item_count and an items array. On a bad shape
        // keep the last good mini-cart instead of blanking it / wrong badge.
        if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) {
          if (window.console && console.warn) console.warn('[aef-mini-cart] refreshFromServer: bad /cart.js shape, keeping last state', cart);
          return;
        }
        var prev = lastCount;
        lastCount = cart.item_count;
        /* ═══ 15/9/2026 (vòng 49) — `total_price` Ở ĐÂY LÀ CỐ Ý. ĐỪNG ĐỔI. ═════
         *
         * Đã có ÍT NHẤT HAI phiếu đề nghị đổi dòng này sang thang ba bậc
         * `items_subtotal_price ?? original_total_price ?? total_price` "cho
         * khớp ba người ghi kia" (vòng 43 — test-cart-total-basis.js đã ghi lại
         * nó như một báo động giả; vòng 49 — phiếu `huy-hieu-tong-2`). Cả hai
         * lần đều BÁC. Lý do, để lần thứ ba khỏi phải tra lại:
         *
         * 1. #CartTotalText LÀ SỐ TIỀN KHÁCH TRẢ, không phải tổng dòng hàng.
         *    sections/header.liquid:261 in nó ra lúc tải trang bằng ĐÚNG
         *    `cart.total_price`, và ae-delivery-widget.css:1373 (trong
         *    `@media min-width:769px`) kéo nó khỏi visually-hidden ⇒ trên 🖥
         *    khách ĐỌC con số này. `refreshFromServer` chạy ngay lúc khởi tạo
         *    (:~592), nên đổi trường ở đây là cãi nhau với con số máy chủ vừa
         *    in, ở MỌI lần tải trang — chứ không chỉ khi khách đụng vào giỏ.
         *    Đo được: cấy đúng bản vá phiếu đề xuất ⇒ ô tiền lắng ở $100.00
         *    trong khi máy chủ vừa in $85.00.
         *
         * 2. THANG BA BẬC LÀ THUỐC CỦA `/cart/change.js`, KHÔNG PHẢI ĐỊNH NGHĨA
         *    KHÁC CỦA Ô TIỀN. Cả ba người ghi dùng nó (cart.js:270, :455 ·
         *    wbcartdropdown.js:180 · nhánh sửa-trần :1126 ngay trong tệp này)
         *    đều đọc từ `/cart/change.js`, nơi `total_price` phồng giả +$10.99
         *    trong 200–400ms với `total_discount: 0` — BUG-H, card-stepper.js:350.
         *    `/cart.js` KHÔNG có bệnh đó. Bê thuốc sang đây là rước cái GIÁ của
         *    nó (`items_subtotal_price` chưa trừ khuyến mãi CẤP GIỎ) mà không
         *    được cái lợi nào.
         *
         * 3. 4 khuyến mãi tự động của mùa trung thu đều là DiscountProducts
         *    (tra Admin API 15/9) = giảm theo DÒNG ⇒ `items_subtotal_price` đã
         *    trừ sẵn ⇒ hai con số BẰNG NHAU, không có cú nhảy nào để chữa.
         *    Chỉ khuyến mãi tự động CẤP ĐƠN mới tách hai số ra, và lúc đó
         *    `total_price` mới là số đúng.
         *
         * Người gác: scripts/test-tien-pill-gio-la-tien-khach-tra.js — nạp và
         * CHẠY tệp này trong jsdom với một giỏ CÓ khuyến mãi cấp giỏ (giỏ thường
         * ba số bằng nhau nên không bày ra được), rồi hỏi con số khách ĐỌC ĐƯỢC.
         * Trường đem so đọc từ header.liquid, không chép. ════════════════════ */
        syncCountAndState(cart.item_count, cart.total_price);
        renderRows(cart.items);
        if (triggerShowOnAdd && cart.item_count > prev) {
          show('add');
        }
      })
      .catch(function () {})
      .then(function () {
        refreshInflight = false;
        if (refreshPending) {
          refreshPending = false;
          var s = pendingShowAdd;
          pendingShowAdd = false;
          refreshFromServer(s);
        }
      });
  }

  function init() {
   try {
    bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    popup = bubble.querySelector('[data-aef-mini-cart]');
    btn = bubble.querySelector('#toggleCountCart');
    if (!popup || !btn) return;

    card = popup.querySelector('[data-aef-mini-cart-card]');
    backdrop = popup.querySelector('[data-aef-mini-cart-backdrop]');
    closeBtn = popup.querySelector('[data-aef-mini-cart-close]');
    countEl = popup.querySelector('[data-aef-mini-cart-count]');
    listContainer = popup.querySelector('#webi-main-cart-items .js-contents');

    // Cart icon is now <a href="/cart"> (BUG-L + BUG-G fix). Native click
    // navigates to /cart on both desktop and mobile per spec §1 row 3. No
    // JS click handler needed — hover still triggers popup separately via
    // mouseenter/mouseleave on the parent .aef-cart-wrap (bubble).
    btn.removeAttribute('data-toggle');
    btn.removeAttribute('data-bs-toggle');
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'aefMiniCart');

    bubble.addEventListener('mouseenter', safe(function () {
      if (isMobile()) return;
      clearHover();
      clearAutoClose();
      show('hover');
    }, 'bubble.mouseenter'));
    bubble.addEventListener('mouseleave', safe(function () {
      if (isMobile()) return;
      clearHover();
      hoverTimer = setTimeout(hide, HOVER_GRACE_MS);
    }, 'bubble.mouseleave'));
    popup.addEventListener('mouseenter', safe(function () {
      if (isMobile()) return;
      clearHover();
      clearAutoClose();
    }, 'popup.mouseenter'));
    popup.addEventListener('mouseleave', safe(function () {
      if (isMobile()) return;
      clearHover();
      hoverTimer = setTimeout(hide, HOVER_GRACE_MS);
    }, 'popup.mouseleave'));

    // BUG-J fix: click-outside close. Re-query the live #cart-icon-bubble
    // each time in case cart-notification.js section render replaced it,
    // which would leave the captured `bubble` reference orphaned and
    // bubble.contains() returning false for valid in-bubble clicks.
    document.addEventListener('click', safe(function (e) {
      if (!isOpen) return;
      var liveBubble = document.getElementById('cart-icon-bubble') || bubble;
      if (liveBubble && liveBubble.contains(e.target)) return;
      hide();
    }, 'document.click'), true);

    if (backdrop) {
      backdrop.addEventListener('click', function (e) { e.preventDefault(); hide(); });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) { e.preventDefault(); hide(); });
    }
    // BUG-I fix: ESC close. Listen on both keydown and keyup (some testing
    // frameworks dispatch only one), and check isOpen at fire time.
    var escHandler = safe(function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) && isOpen) {
        hide();
      }
    }, 'document.escape');
    document.addEventListener('keydown', escHandler);
    document.addEventListener('keyup', escHandler);

    // Mobile swipe-down close. Build #4 polish: loosened threshold to 40px
    // (was 70) and 800ms window (was 600) so a quick flick from anywhere
    // in the sheet dismisses cleanly. Only triggers when the card is at
    // scrollTop 0 so internal scrolling near the top doesn't double-fire
    // as a dismiss gesture.
    var touchStartY = null;
    var touchStartT = 0;
    if (card) {
      card.addEventListener('touchstart', function (e) {
        if (!isMobile()) return;
        touchStartY = e.touches[0].clientY;
        touchStartT = Date.now();
      }, { passive: true });
      card.addEventListener('touchmove', function (e) {
        if (!isMobile() || touchStartY === null) return;
        var dy = e.touches[0].clientY - touchStartY;
        if (dy > 40 && Date.now() - touchStartT < 800 && card.scrollTop <= 0) {
          hide();
          touchStartY = null;
        }
      }, { passive: true });
      card.addEventListener('touchend', function () { touchStartY = null; });
    }

    // ===== ADD-TO-CART DETECTION (observer + events only — no prototype patching) =====

    // Trigger 1 — MutationObserver on #Cartcount badge.
    // The bulletproof signal: cart-card-indicator.js updates the badge text
    // after every cart mutation regardless of how /cart/add was invoked
    // (form POST, jQuery $.ajax, fetch, Shopify Buy SDK). Cannot interfere
    // with any theme code path because we only OBSERVE — no patching.
    var badge = document.getElementById('Cartcount');
    if (badge && 'MutationObserver' in window) {
      var lastBadge = parseInt((badge.textContent || '0').trim(), 10) || 0;
      var badgeObs = new MutationObserver(function () {
        var newBadge = parseInt((badge.textContent || '0').trim(), 10) || 0;
        // BUG-T (build #9): mirror textContent → data-count so the empty-cart
        // hide CSS rule works regardless of which writer touched the badge
        // (cart-card-indicator.js / wbcartdropdown.js / cart-notification.js /
        // perf-instant.js all set textContent only — they don't know about
        // data-count). Centralising the attribute update here means we don't
        // have to fork-modify every cart writer in the theme.
        if (String(newBadge) !== badge.getAttribute('data-count')) {
          badge.setAttribute('data-count', String(newBadge));
        }
        /* 14/9/2026 (vòng 48) — CHỈ NGHE NGƯỜI LẠ. Nếu con số đang thấy đúng là
         * con số `syncCountAndState` vừa tự ghi thì đây là ngòi bút nhà mình:
         * hạ `lastBadge` cho khớp rồi đi ra, KHÔNG đi hỏi /cart.js. Không có
         * dòng này thì lượt ghi lạc quan lúc bấm tự châm ngòi cho một /cart.js
         * chạy đua với /cart/add.js, và vì /cart.js nhẹ hơn (70ms vs 320ms) nó
         * luôn về trước với trạng thái CHƯA THÊM ⇒ huy hiệu tắt ngóm ~255ms.
         * Lượt hoà giải thật của máy chủ vẫn tới bằng đường `cart:updated`
         * (:~520) và bằng cart-card-indicator.js — không mất lượt chốt sổ nào. */
        var laTuGhi = tuGhiHuyHieu > 0 && String(newBadge) === tuGhiGiaTri;
        // Một lượt giao gom HẾT bản ghi đang xếp hàng ⇒ mọi lượt tự ghi trước đó
        // coi như đã đọc xong. Hạ về 0 ngay, cờ không kẹt lại sang lượt sau.
        tuGhiHuyHieu = 0;
        tuGhiGiaTri = null;
        if (laTuGhi) {
          lastBadge = newBadge;
          return;
        }
        if (newBadge > lastBadge) {
          refreshFromServer(true);
        } else if (newBadge !== lastBadge) {
          refreshFromServer(false);
        }
        lastBadge = newBadge;
      });
      badgeObs.observe(badge, { childList: true, characterData: true, subtree: true });
    }

    // Trigger 2 — custom events dispatched by theme code paths
    document.addEventListener('cart:updated', function (e) {
      if (e && e.detail && typeof e.detail.count === 'number') {
        var prev = lastCount;
        // `source: 'cart-page'` (assets/cart.js) — khách đang đứng NGAY ở trang
        // giỏ và vừa đổi số lượng. Vẫn đồng bộ badge + tổng, nhưng KHÔNG bật
        // popup: đẩy một bảng giỏ nhỏ che lên bảng giỏ lớn là quấy khách.
        var showOnAdd = e.detail.count > prev && e.detail.source !== 'cart-page';
        lastCount = e.detail.count;
        syncCountAndState(e.detail.count, e.detail.total);
        refreshFromServer(showOnAdd);
      } else {
        refreshFromServer(true);
      }
    });
    ['cart:item-added', 'product:added-to-cart', 'cart:added'].forEach(function (ev) {
      document.addEventListener(ev, safe(function () { refreshFromServer(true); }, ev));
    });

    // 2026-08-16 — đổi ngôn ngữ thì vẽ lại. refreshFromServer(false) = vẽ lại, KHÔNG
    // bật popup. (27/9/2026: nhãn dòng đếm nay là data-i18n tĩnh nên tự đổi tiếng;
    // người nghe này không còn lo phần đó — gỡ hay giữ là việc riêng, chưa quyết.)
    document.addEventListener('aef:lang-changed', safe(function () { refreshFromServer(false); }, 'lang'));

    // 1/11/2026 (fix-gio) — trang về từ bfcache: huy hiệu + tổng là số lúc rời đi, giỏ có thể đã đổi ở
    // trang khác. Chỉ khi persisted, không bật popup (giỏ đổi ở nơi khác, không phải cú thêm vừa rồi).
    // Lý do đầy đủ + người gác: khối pageshow của card-stepper.js.
    window.addEventListener('pageshow', safe(function (e) {
      if (e && e.persisted) refreshFromServer(false);
    }, 'pageshow'));

    // Initial sync — populates lastCount + renders rows from current cart state
    refreshFromServer(false);
   } catch (e) {
     if (window.console && console.warn) {
       console.warn('[aef-mini-cart] init error — popup will not work, but the rest of the page is unaffected:', e);
     }
   }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
 * BUG-M + BUG-O FIX (build #9, 2026-05-17) — Window-level cart-add queue
 *
 * Build #8 used a module-scoped `var addQueue` inside an IIFE that ran once.
 * That is effectively global, but Cowork's edge-case stress test still saw
 * 5 rapid clicks across 5 DIFFERENT products result in only 2/5 persisting
 * (first + last). Whatever the root cause, Boss prescribed making the queue
 * explicitly window-level (Boss-recommended exact pattern):
 *
 *   window.__aefCartAddQueue = window.__aefCartAddQueue || Promise.resolve();
 *   window.__aefCartAddQueue = window.__aefCartAddQueue.then(function () {
 *     return fetch('/cart/add.js', { method: 'POST', body: formData });
 *   });
 *
 * Why window-level matters beyond style:
 *   - Survives the IIFE bound-guard pattern even if some other script set
 *     `__aefAddQueueBound = true` before us
 *   - Any other future code path (cart drawer, quick-view, etc.) can append
 *     to the same chain so we never have two queues racing in parallel
 *
 * Build #9 also adds a 50 ms post-response settle delay before yielding the
 * chain to the next link. /cart/add.js HTTP 200 returns as soon as Shopify's
 * web tier acks the write, but the cart-cookie session lock can briefly
 * remain held while the change commits. Sequential fetches firing in immediate
 * succession occasionally observed partial loss in earlier QA — the settle
 * delay closes that window. 50 ms × 5 adds = 250 ms total added latency,
 * imperceptible against the popup's 4.5 s auto-close.
 *
 * Background context — product card "+" button is rendered INSIDE .wbimgblock
 * (image container) for absolute positioning, linked to its product-form via
 * HTML5 `form="..."` attribute. resolveForm() walks btn.form (which honours
 * that attribute) so the hijack works regardless of DOM tree position.
 * ========================================================================== */
(function () {
  // Initialize the window-level queue (idempotent). Don't bail out via a
  // bound-guard for the queue itself — the queue is shared state across
  // potentially-multiple loads. Only bail on listener registration.
  window.__aefCartAddQueue = window.__aefCartAddQueue || Promise.resolve();

  if (window.__aefAddQueueListenerBound) return;
  window.__aefAddQueueListenerBound = true;

  var SETTLE_MS = 50; // small delay between sequential /cart/add requests
  var pendingPerBtn = new WeakMap(); // btn → count (for loading state)

  // BUG-Z (2026-05-21) — Global pending counter shared with
  // cart-card-indicator.js. While this is > 0 the indicator's syncFromCart
  // skips overwriting #Cartcount, so the optimistic badge bump below isn't
  // flickered down to the server-truth-at-this-instant in between rapid
  // clicks. The LAST queued add's syncFromCart (which fires after the
  // counter returns to 0) is the one that reconciles the final count.
  if (typeof window.__aefCartAddPending !== 'number') {
    window.__aefCartAddPending = 0;
  }

  /* 14/9/2026 (vòng 48) — SỔ LỜI HỨA CỦA RIÊNG HUY HIỆU.
   * Đếm số lượt mà tệp này đã ghi trước một con số máy chủ CHƯA xác nhận. Sàn
   * chống tụt trong `syncCountAndState` (:291) đọc biến này. Tách hẳn khỏi
   * `__aefCartAddPending` vì cờ kia còn được card-stepper.js:308 và
   * aef-cart-drawer.js:341/494/1530 tăng cho những lượt ghi giỏ KHÁC (có lượt
   * làm giỏ giảm) — mượn nó làm sàn là chặn nhầm đường giảm hợp lệ. */
  if (typeof window.__aefHuaThemHuyHieu !== 'number') {
    window.__aefHuaThemHuyHieu = 0;
  }

  /* Đọc LẠI hai ô header tại đúng thời điểm cần, thay cho ảnh chụp lúc bấm.
   * Cùng cách tra `getElementById` mà syncCountAndState đang dùng (:342) —
   * header chỉ render MỘT lần mỗi trang nên không vướng Pattern 3. */
  function docHuyHieu() {
    var el = document.getElementById('Cartcount');
    return el ? (parseInt((el.textContent || '0').trim(), 10) || 0) : 0;
  }
  function docTienHeader() {
    var el = document.getElementById('CartTotalText');
    return el ? parsePriceCents(el.textContent) : 0;
  }

  function getQtyFromForm(form) {
    if (!form) return 1;
    // BUG minmax (2026-06-06): form.elements resolves HTML5 form=""-associated
    // inputs that live OUTSIDE the <form> tag (the PDP quantity input) —
    // querySelector missed those, so PDP adds with qty > 1 under-counted the
    // optimistic badge delta. querySelector kept as fallback.
    var q = (form.elements && form.elements.namedItem('quantity')) ||
            form.querySelector('input[name="quantity"]');
    if (!q || !q.value) return 1;
    var n = parseInt(q.value, 10);
    return n > 0 ? n : 1;
  }

  // BUG minmax (2026-06-06): user-facing note when the queue clamps/corrects a
  // quantity against the product's min/max rules. Reuses the PDP/cart error
  // element (.show__err--<productId>) + the stepper i18n keys via the aefT
  // bridge (ae-delivery-widget.js) so the message follows the current ae_lang.
  // 18/8/2026 — thêm tham số `qtyEl`: chính ô số lượng mang data-min-qty mà
  // queueAdd vừa dùng để kẹp. Nó cũng mang data-unit-price, nên câu min dựng
  // được thành tiền qua window.aefTienToiThieu (layout/theme.liquid) — cùng một
  // cửa với cart.js và product-form.js. Nhánh 'max' không cần giá.
  function notifyQtyRule(form, kind, limit, qtyEl) {
    try {
      var pidEl = form.elements ? form.elements.namedItem('product-id') : null;
      var pid = pidEl && pidEl.value;
      if (!pid) return;
      var el = document.querySelector('.show__err--' + pid);
      if (!el) return;
      el.style.display = 'block';
      el.textContent = kind === 'max'
        ? ((window.aefT ? window.aefT('card.max_tooltip') : 'Max reached') + ' (' + limit + ')')
        : (window.aefTienToiThieu
            ? window.aefTienToiThieu(limit, window.aefGiaDonVi(qtyEl))
            : (window.aefT ? window.aefT('card.min_hint') : 'Min order:') + ' ' + limit);
    } catch (e) {}
  }

  function dispatchCartUpdated(line, count, total, anhGio, lacQuan) {
    var detail = { source: 'aef-add-queue', line: line || null };
    if (typeof count === 'number') detail.count = count;
    if (typeof total === 'number') detail.total = total;
    /* `anhGio` CHỈ ở lượt sửa trần (thân /cart/change.js = cả giỏ máy chủ) — thanh
       free-ship nhận làm bản chụp (aef-fsb.js nhanAnhSuKien). Lượt chốt lạc quan
       cuối queueAdd KHÔNG có giỏ máy chủ nào ⇒ không đính, đừng bịa. */
    if (anhGio) detail.anhGio = anhGio;
    /* 9/10/2026 — `lacQuan`: lượt chốt cuối queueAdd KHAI rằng count/total của nó là số đọc
       lại từ huy hiệu + tổng header (ghi lạc quan lúc bấm), máy chủ CHƯA xác nhận giỏ nào.
       Thanh free-ship và khối đóng khe nhờ đó GIỮ lần vẽ cuối tới khi GET /cart.js về (aef-fsb.js,
       "LƯỢT LẠC QUAN CÓ KHAI"), thay vì vẽ tổng chưa xác nhận rồi chớp "Add $X…"/khung chờ.
       Sự kiện vẫn phát NGAY (thẻ đổi sang ô tăng/giảm, toast, huy hiệu — BUG-F không chậm). */
    if (lacQuan === true && typeof count === 'number' && typeof total === 'number') detail.lacQuan = lacQuan;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail }));
    } catch (e) {
      var evt = document.createEvent('Event');
      evt.initEvent('cart:updated', true, true);
      document.dispatchEvent(evt);
    }
  }

  /* BUG-C (2026-05-29): convert a price text node ("$2.99", "USD 14.95",
     "$1,499.00") to integer cents. Returns 0 if no parseable number found. */
  function parsePriceCents(txt) {
    if (txt == null) return 0;
    var m = String(txt).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    if (!m) return 0;
    return Math.round(parseFloat(m[1]) * 100);
  }

  /* BUG-C: unit price for the line being added, in cents. The card path
     reads the price element inside the surrounding .wbproduct-container;
     the PDP path reads the .mainp-price block in the product section.
     Returns 0 when no price can be found, which causes queueAdd to skip
     the optimistic total update (badge still updates; total catches up on
     the cart-card-indicator reconciliation). */
  function unitPriceCentsFor(btn) {
    if (btn && btn.closest) {
      var card = btn.closest('.wbproduct-container');
      if (card) {
        var p = card.querySelector('.price-item--sale, .price-item--regular');
        if (p) return parsePriceCents(p.textContent);
      }
    }
    /* 🔴 30/10/2026 (làn gio-2) — ĐỌC Ô GIÁ KHÁCH ĐANG THẤY, KHÔNG ĐỌC Ô ĐỨNG ĐẦU TÀI LIỆU.
       Bộ chọn cũ trả phần tử ĐẦU khớp theo thứ tự tài liệu = ô .price__regular, kể cả khi ô đó
       đang ẨN. Thường vô hại vì hai ô in cùng một số, nhưng app Shopify Subscriptions
       (showSalePrice) ghi giá GẠCH vào MỌI .price-item--regular, gồm cả ô ẩn đó ⇒ thêm 3 món
       gói tháng: tổng header nháy $5.97 rồi /cart.js kéo về $5.67. Hàng ĐANG GIẢM trong nhóm gói
       còn nặng hơn: app ghi ngay lúc tải, mua một lần cũng nháy giá chưa giảm ($5.97 → $4.50).
       .price--on-sale do máy chủ in và app bật/tắt theo lựa chọn mua, CSS cũng dựa vào nó để
       chọn ô hiện ⇒ đọc theo nó là đọc đúng con số khách đang nhìn (app đặt từ data-variant-price
       — cùng nguồn với dòng "Min order" của aef-moq-relax.js).
       Giữ bộ chọn cũ làm đường lùi cho khối giá không có .price.
       Người gác: scripts/test-tong-header-them-goi-dinh-ky.js */
    var khoiGia = document.querySelector('.mainp-price .price');
    var pdp = khoiGia && khoiGia.querySelector(khoiGia.classList.contains('price--on-sale') ? '.price-item--sale' : '.price__regular .price-item--regular');
    if (!pdp) pdp = document.querySelector('.mainp-price .price-item--sale, .mainp-price .price-item--regular');
    if (pdp) return parsePriceCents(pdp.textContent);
    return 0;
  }

  /* 2026-08-17 — nút mua PDP HOÁ TRẮNG TRƠN trong lúc đang gửi.
   *
   * `.button.loading` trong base.css:652-667 làm `color: transparent` và
   * `opacity: 0` cho mọi <span> con, rồi hẹn spinner thế chỗ:
   *     .button.loading .loading-overlay__spinner svg { opacity: 1 }
   * Nhưng chính cái <div class="loading-overlay__spinner hidden"> đó mang sẵn
   * class `hidden` = `display: none !important` (base.css:50), và chỗ DUY NHẤT
   * gỡ `hidden` là product-form.js:34 — MÃ CHẾT: listener click-capture ở cuối
   * file này gọi stopImmediatePropagation nên sự kiện `submit` không bao giờ
   * xảy ra, product-form.js không bao giờ chạy.
   *
   * Kết quả trên PDP thật: bấm "Add to cart" → nút thành một ô mờ 50%, KHÔNG
   * chữ, KHÔNG spinner, suốt 400–1500ms. Trông y như hỏng ⇒ khách bấm lại.
   * Đây là nửa còn thiếu của trạng thái loading, không phải chuyện thẩm mỹ.
   *
   * Nút "+" trên thẻ (.card__quick-add) KHÔNG mang class `button` nên cả khối
   * CSS trên không khớp — nó vẫn câm, và đó là lỗi riêng, vá ở commit sau. */
  function setLoading(btn, on) {
    if (!btn) return;
    var spinner = btn.querySelector && btn.querySelector('.loading-overlay__spinner');
    if (on) {
      btn.classList.add('loading');
      btn.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  /* 2026-08-17 — dấu ✓ "đã thêm", nay bắn ở NHÁNH THÀNH CÔNG.
   *
   * Trước đây perf-instant.js:205 gắn class này ngay lúc CLICK, vô điều kiện.
   * Chuyển về đây vì đây là chỗ DUY NHẤT biết máy chủ có nhận hay không, và để
   * cả ba trạng thái của nút (đang gửi · đã thêm · lỗi) do đúng một file ghi —
   * Pattern 1 trong hàng rào chống hồi quy, cùng cái đã đẻ ra BUG-7 và BUG-C.
   *
   * Vẫn 900ms như cũ để không đổi cảm giác quen thuộc và không phá luật CSS
   * `.is-added` sẵn có (aef-product-card.css:168 vòng tròn xanh + :752 bản thanh
   * ngang trang chủ). Ở đây KHÔNG đụng gì tới nhánh lỗi: toast đỏ của
   * aefShowAddError đã lo, thêm chữ lên nút nữa là hai giọng cùng nói. */
  function flashAdded(btn) {
    if (!btn || !btn.classList) return;
    btn.classList.add('is-added');
    setTimeout(function () { btn.classList.remove('is-added'); }, 900);
  }

  function bumpPending(btn, delta) {
    var n = (pendingPerBtn.get(btn) || 0) + delta;
    pendingPerBtn.set(btn, n);
    if (n > 0) setLoading(btn, true);
    else setLoading(btn, false);
    return n;
  }

  function settle(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // Locate the host <form> for a submit button, honouring HTML5 form="..."
  // attribute (button can live outside its form). HTMLButtonElement.form
  // already resolves this for us; fall back to manual id lookup for
  // pathological cases or unattached buttons.
  function resolveForm(btn) {
    if (!btn) return null;
    var form = btn.form || null;
    if (!form) {
      var fid = btn.getAttribute('form');
      if (fid) form = document.getElementById(fid);
    }
    if (!form) form = btn.closest && btn.closest('form');
    return form || null;
  }

  function queueAdd(form, btn) {
    var formData;
    try {
      formData = new FormData(form);
    } catch (e) {
      return; // Form gone or detached — nothing to queue
    }
    // BUG minmax (2026-06-06): this queue hijacks EVERY product-form submit at
    // click-capture (listener at the bottom of this file), so product-form.js's
    // min/max checks never run on the live add path — THIS is the one real
    // gate. Clamp the request into the product's [min..max]. Rules ride on the
    // form's quantity input (data-min-qty/data-max-qty, main-product.liquid);
    // form.elements resolves the HTML5 form=""-associated PDP input that lives
    // outside the <form>. Card forms carry no data attrs -> sentinel defaults
    // -> untouched (card-stepper owns post-add changes there).
    var qtyRuleEl = form.elements ? form.elements.namedItem('quantity') : null;
    var ruleMin = qtyRuleEl && qtyRuleEl.getAttribute ? parseInt(qtyRuleEl.getAttribute('data-min-qty'), 10) : NaN;
    var ruleMax = qtyRuleEl && qtyRuleEl.getAttribute ? parseInt(qtyRuleEl.getAttribute('data-max-qty'), 10) : NaN;
    if (!(ruleMin >= 1)) ruleMin = 1;
    if (!(ruleMax >= 1)) ruleMax = 0; // 0 = no cap (same sentinel as card-stepper rulesFor)
    var reqQty = parseInt(formData.get('quantity'), 10);
    if (!(reqQty >= 1)) reqQty = 1;
    var clampedQty = reqQty;
    if (clampedQty < ruleMin) clampedQty = ruleMin;
    if (ruleMax > 0 && clampedQty > ruleMax) clampedQty = ruleMax;
    if (clampedQty !== reqQty) {
      formData.set('quantity', String(clampedQty));
      if (qtyRuleEl) qtyRuleEl.value = String(clampedQty);
      notifyQtyRule(form, clampedQty < reqQty ? 'max' : 'min', clampedQty < reqQty ? ruleMax : ruleMin, qtyRuleEl);
    }

    // Drop product-form.js-only fields the /cart/add endpoint doesn't want.
    formData.delete('sections');
    formData.delete('sections_url');
    formData.delete('product-id');

    bumpPending(btn, +1);

    // BUG-7 (2026-05-29) — Optimistic header badge bump removed. The "+" quick-
    // add click also fires perf-instant.js's capture-phase listener which
    // independently wrote Cartcount.textContent; both writes racing produced
    // a brief "2" flash on a single click (badge 0 → 1 → 2 → 0 → 1 final).
    // Badge is now server-driven only — via syncCountAndState on the
    // cart:updated event or cart-card-indicator's syncFromCart 200ms after
    // the /cart/add.js response. The pending counter is still incremented
    // so cart-card-indicator skips reconciling mid-queue (rapid 5-click
    // bursts would otherwise have each /cart.js race against the next add).
    var qty = getQtyFromForm(form);
    window.__aefCartAddPending = (window.__aefCartAddPending || 0) + 1;

    /* ═══ 6/9/2026 (vòng 43) — TRẢ LẠI CÚ NHẢY TỨC THÌ CHO HUY HIỆU ═══════════
     *
     * ĐO ĐƯỢC trên LIVE: bấm nút thẻ SP → nút phản hồi sau 52ms, nhưng HUY HIỆU
     * SỐ MÓN đứng im **549ms**. Nửa giây đó là do thiết kế, không phải mạng chậm:
     * huy hiệu chỉ được viết SAU khi /cart/add.js trả lời, rồi còn qua
     * `setTimeout(syncFromCart, 200)` của cart-card-indicator.js:55 và MỘT vòng
     * /cart.js nữa. Cộng lại ≈ vòng-add + 200ms + vòng-cart.
     *
     * VÌ SAO GIỜ MỚI TRẢ LẠI ĐƯỢC — căn bệnh của BUG-7 đã bị gỡ, còn liều thuốc
     * thì chưa. Khối chú thích ngay trên nói rõ BUG-7 là do perf-instant.js CŨNG
     * ghi Cartcount ở pha capture ⇒ hai ngòi bút tranh nhau ⇒ nháy "2". Nhưng
     * assets/perf-instant.js:223-260 đã THÔI ghi từ 17/8/2026 (chú thích tại chỗ:
     * "ai thật sự BIẾT kết quả thì người đó vẽ"). Nay chỉ còn MỘT ngòi bút, nên
     * cách chữa "bỏ hẳn cập nhật tức thì" là liều quá tay còn sót lại.
     *
     * VẪN ĐÚNG P1 — MỘT NGƯỜI GHI: vẫn là `syncCountAndState()` của chính tệp
     * này, không đẻ thêm nơi ghi nào. Cụ thể KHÔNG bắn `cart:updated` sớm —
     * sự kiện đó có 10 nơi nghe, vài nơi trong đó đi hỏi /cart.js, mà lúc này
     * máy chủ CHƯA cộng hàng ⇒ chúng sẽ đọc ra trạng thái TRƯỚC-KHI-THÊM rồi ghi
     * đè về. Đó đúng là BUG-C (tổng tiền dao động $0 ↔ $2,99 sáu lần trong 750ms).
     * Gọi thẳng hàm ghi, im lặng, không bắn sự kiện.
     *
     * VÌ SAO KHÔNG BỊ cart-card-indicator GHI ĐÈ: nó chỉ ghi khi
     * `__aefCartAddPending === 0` (cart-card-indicator.js:22-24), mà biến đó vừa
     * được tăng ở dòng ngay trên. Hàng đợi cạn thì nó mới chốt lại bằng số của
     * máy chủ — đúng vai trò hoà giải.
     *
     * NỬA CÒN LẠI CỦA LUẬT LẠC QUAN — LÙI LẠI KHI HỎNG. Hứa trước mà không rút
     * lời khi máy chủ từ chối thì chính là "lời hứa giả" mà chú thích ở nhánh
     * !res.ok đã đặt tên. Nên chụp lại số CŨ ở đây và trả về đúng nó ở cả hai
     * nhánh hỏng (422 và mất mạng).
     * ══════════════════════════════════════════════════════════════════════ */
    var badgeEl = document.getElementById('Cartcount');
    var demCu = badgeEl ? (parseInt((badgeEl.textContent || '0').trim(), 10) || 0) : 0;
    var totalElCu = document.getElementById('CartTotalText');
    var tienCu = totalElCu ? parsePriceCents(totalElCu.textContent) : 0;
    var donGia = unitPriceCentsFor(btn);
    var tienMoi = donGia > 0 ? tienCu + qty * donGia : null;
    /* Đơn giá không đọc được thì THÔI ĐỘNG VÀO TIỀN — để nguyên số cũ còn hơn in
       một con số bịa. Huy hiệu SỐ MÓN thì luôn biết chắc: qty là thứ mình vừa gửi. */
    /* Gọi qua cầu nối `window.aefGhiSoGio` (đặt ở khối popup, cùng tệp này) — hai
       IIFE không chung phạm vi nên gọi thẳng tên hàm là ReferenceError. Tra window
       LÚC CHẠY chứ không giữ tham chiếu lúc nạp: khối popup dựng cầu nối khi nó
       chạy, và nếu vì lý do gì đó nó không chạy thì lượt ghi lạc quan im lặng bỏ
       qua — thêm hàng vẫn phải hoạt động bình thường, chỉ là mất cú nhảy tức thì. */
    /* `nhan0` (1/11/2026, fix-gio): chỉ lượt RÚT lời hứa mới được ghi tổng $0.00. Giỏ trống bấm Add bị từ
       chối: rút xong tổng đúng bằng 0, mà cổng `tien > 0` nuốt nó nên #CartTotalText đứng ở số vừa hứa
       cạnh huy hiệu 0, tới lượt GET kế tiếp (mất mạng thì tới khi tải lại trang). Người gác: T8 của
       scripts/test-gio-luot-cu-khong-ve-de.js */
    function ghiSo(dem, tien, nhan0) {
      try {
        if (typeof window.aefGhiSoGio === 'function') {
          var ghiDuoc = typeof tien === 'number' && (tien > 0 || (nhan0 === true && tien === 0));
          window.aefGhiSoGio(dem, ghiDuoc ? tien : undefined);
          return true;
        }
      } catch (e) {}
      return false;
    }
    var daGhiLacQuan = ghiSo(demCu + qty, tienMoi);

    /* ═══ 14/9/2026 (vòng 48) — LỜI HỨA PHẢI CÓ SỔ, VÀ PHẢI CÓ HẠN ═══════════
     *
     * Vừa ghi trước một con số máy chủ chưa xác nhận ⇒ ghi vào sổ. Sàn chống tụt
     * ở `syncCountAndState` (:291) nhờ đó biết mọi câu trả lời thấp hơn trong
     * quãng này đều là tin ôi. Trả sổ ĐÚNG MỘT LẦN cho mỗi lượt bấm — dù lượt
     * này kết thúc bằng thành công, 422, hay mất mạng.
     *
     * CÓ HẠN: nếu /cart/add.js treo vĩnh viễn (📱 rớt sóng giữa chừng) thì không
     * nhánh nào chạy tới, sổ không bao giờ được trả, và sàn sẽ khoá huy hiệu ở
     * con số lạc quan mãi mãi. Hẹn 15s tự trả — dài hơn mọi vòng mạng thật, đủ
     * ngắn để khách không phải sống với một con số đóng băng. Hàm trả sổ tự
     * chống gọi hai lần nên cái hẹn này vô hại khi mạng bình thường. */
    var daTraHua = false;
    function traHua() {
      if (daTraHua || !daGhiLacQuan) return;
      daTraHua = true;
      window.__aefHuaThemHuyHieu = Math.max(0, (window.__aefHuaThemHuyHieu || 1) - 1);
    }
    if (daGhiLacQuan) {
      window.__aefHuaThemHuyHieu = (window.__aefHuaThemHuyHieu || 0) + 1;
      setTimeout(traHua, 15000);
    }

    /* Rút lời hứa: ĐỌC LẠI rồi TRỪ ĐÚNG PHẦN MÌNH ĐÃ HỨA — không khôi phục ảnh
     * chụp `demCu`/`tienCu` lúc bấm nữa. Ảnh chụp đó đã ôi: giữa tràng bấm, một
     * cú 422 khôi phục nó là xoá luôn mọi lượt thêm THÀNH CÔNG xảy ra sau đó.
     * ĐO ĐƯỢC trên bản trước: tràng 3 cú mà cú ĐẦU bị 422 ⇒ dãy huy hiệu
     * 1 → 2 → 0 → 1 → 0 → 2 → 1 → 2, tức sập về 0 hai lần trong khi máy chủ
     * đang giữ 1 rồi 2 món. */
    function luiLaiHuyHieu() {
      if (!daGhiLacQuan) return;
      traHua();
      var dem = docHuyHieu() - qty;
      if (dem < 0) dem = 0;
      var tien = null;
      if (typeof tienMoi === 'number' && donGia > 0) {
        tien = docTienHeader() - qty * donGia;
        /* Âm = ô tiền đã bị lượt đọc máy chủ ghi đè, không còn gồm phần mình hứa: để nguyên, đừng ghi $0.00. */
        if (tien < 0) tien = null;
      }
      /* Sàn chống tụt (:291) sinh ra để chặn TIN ÔI của máy chủ. Lượt RÚT này
       * không phải tin ôi: nó trừ đi đúng phần MÌNH vừa hứa, tính trên con số
       * đang hiện ngay lúc này. Hạ sàn đúng một lượt ghi đồng bộ rồi dựng lại —
       * nếu không, giữa tràng bấm lời rút sẽ bị chính sàn nuốt và khách phải chờ
       * tới lượt hoà giải cuối mới thấy máy chủ đã từ chối. */
      var giuSo = window.__aefHuaThemHuyHieu;
      window.__aefHuaThemHuyHieu = 0;
      try { ghiSo(dem, tien, true); } finally { window.__aefHuaThemHuyHieu = giuSo; }
    }

    // Snapshot the variant id for diagnostic logging — Cowork can correlate
    // expected vs actual when debugging future edge cases.
    var snapshotId = (form.querySelector('input[name="id"]') || {}).value || '?';

    /* Máy chủ CÓ NHẬN KHÔNG — cờ riêng của lượt gọi này. Đọc `r.ok` chứ đừng
     * đọc "line có null không": một lần add THÀNH CÔNG mà thân phản hồi lỗi cú
     * pháp cũng cho line = null, và khi đó dán dấu ✓ vẫn là nói đúng sự thật.
     * Cùng lý lẽ đã ghi ở aef-cart-drawer.js:345-350. */
    var addOk = false;

    window.__aefCartAddQueue = window.__aefCartAddQueue
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
      })
      .then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          return { ok: r.ok, status: r.status, body: body };
        });
      })
      .then(function (res) {
        if (!res.ok) {
          if (window.console && console.warn) {
            console.warn('[aef-add-queue] /cart/add HTTP ' + res.status +
              ' for variant ' + snapshotId, res.body);
          }
          /* 2026-08-16: trước đây tới đây là HẾT — chỉ một dòng console.warn mà
             khách không bao giờ thấy. Trong khi đó BA tín hiệu THÀNH CÔNG đã
             chạy xong từ trước, vô điều kiện, ngay lúc bấm: dấu ✓ xanh 900ms
             trên nút (perf-instant.js), ảnh bay vào giỏ, và badge nảy "+1".
             Máy chủ từ chối thì cả ba vẫn diễn — khách tin là đã thêm được.
             Dựng lại: /collections/on-sale → variant 47180379324722
             (min_qty=2 mà stock=1) → 422 "maximum quantity… already in cart".
             Đây là nửa "rollback" còn thiếu của luật optimistic UI, và là
             ĐÚNG lỗi đã vá ngày 14/8 ở đường thêm hàng kia (aef-cart-drawer.js
             :315) — đường này bị bỏ sót vì nó có hàng đợi riêng.
             Tra window LÚC CHẠY chứ không giữ tham chiếu lúc nạp: file này nạp
             TRƯỚC aef-app.js (chứa aef-cart-drawer.js), lúc nạp chưa có hàm. */
          try { if (window.aefShowAddError) window.aefShowAddError(res.status); } catch (e) {}
          /* Máy chủ TỪ CHỐI ⇒ rút lại con số đã hứa ở lúc bấm. Không có dòng này
             thì huy hiệu đứng sai cho tới lượt hoà giải sau — đúng kiểu "lời hứa
             giả" mà chú thích ngay trên đã đặt tên. */
          luiLaiHuyHieu();
          return null;
        }
        addOk = true;
        return res.body;
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[aef-add-queue] variant ' + snapshotId + ' failed:', err);
        }
        /* .catch NÀY chỉ ôm đúng lệnh /cart/add ở trên (fetch + đọc body +
           nhánh !res.ok), tức là mọi thứ rơi vào đây đều là "thêm hàng hỏng"
           — báo cho khách là đúng. Các lời gọi mạng SAU khi add đã thành công
           (cap-correct /cart/change.js) có .catch riêng của chúng ở dưới, nên
           không có chuyện add xong rồi vẫn hiện "không thêm được". */
        try { if (window.aefShowAddError) window.aefShowAddError(0); } catch (e) {}
        /* Mất mạng / fetch vỡ ⇒ cũng phải rút lại con số đã hứa. */
        luiLaiHuyHieu();
        return null;
      })
      .then(function (line) {
        // BUG-B v2 (2026-05-29): once the server confirms the add via the
        // /cart/add.js response, compute the new badge count as currentBadge
        // + qty (the REQUEST quantity, i.e. what we asked to add) and pass
        // it on the cart:updated event. The listener below picks count out
        // of detail and writes the badge via syncCountAndState immediately
        // — no extra /cart.js round-trip needed. Cuts ~300-500ms off the
        // badge update path that BUG-7 introduced when it removed the pre-
        // response optimistic write. cart-card-indicator's syncFromCart
        // still reconciles after the queue drains.
        //
        // BUG-B v1 used line.quantity here and overshot on rapid bursts:
        // /cart/add.js response.line.quantity is the CUMULATIVE quantity of
        // this variant in cart AFTER the add (e.g. cart had 1 of variant X
        // + add 1 → response shows quantity=2, not the delta of 1). So
        // 5 rapid clicks added 1+2+3+4+5=15 instead of 1+1+1+1+1=5 to the
        // badge across the queue, producing the 1→3→2→5→3→7→4→9→5 dance
        // (each over-count corrected by refreshFromServer). qty is the
        // value we read from the form before posting, which is the actual
        // delta of this request — monotonic and right.
        // BUG-C (2026-05-29): also compute the new header total in cents
        // and pass it on the same cart:updated event so syncCountAndState
        // writes badge + total in the same call — same frame. Earlier,
        // perf-instant wrote CartTotalText optimistically on click and the
        // refresh path overwrote it with the still-empty server total,
        // bouncing $0 ↔ $2.99 six times in 750ms. Total now follows the
        // BUG-B v2 invariant: queue is sequential, so currentTotal at this
        // .then(line) reflects the previous resolve, and curTotal + qty *
        // unitCents is the right delta. unitCents=0 (price not found in
        // DOM) gracefully skips the optimistic total — badge still updates,
        // total catches up on the cart-card-indicator reconciliation.
        // BUG minmax (2026-06-06): /cart/add is CUMULATIVE — repeat-adds can
        // push the variant line over the per-product cap even though each
        // request alone was within it (5 in cart + add 5 = 10). line.quantity
        // is the authoritative cumulative qty; when it exceeds the cap, chain
        // an ABSOLUTE /cart/change.js down to the cap on this SAME serialized
        // queue (no parallel writers, anti-regression Pattern 1) and dispatch
        // the corrected CART STATE (items_subtotal_price per BUG-H) instead of
        // the delta badge math below — the delta would be wrong here anyway.
        /* 14/9/2026 (vòng 48) — TRẢ SỔ LỜI HỨA TRƯỚC MỌI LƯỢT DISPATCH của lượt
         * này. Từ đây trở xuống, mọi con số đều là con số máy chủ vừa xác nhận
         * hoặc con số đang hiện, không còn gì "hứa mà chưa trả" của riêng lượt
         * này nữa. Trả TRƯỚC khi dispatch để lượt hoà giải cuối cùng (khi hàng
         * đợi cạn, sổ về 0) không bị chính sàn chống tụt chặn lại — đó là lượt
         * duy nhất được phép kéo huy hiệu về đúng số thật của máy chủ.
         * Nhánh 422 / mất mạng đã trả sổ trong `luiLaiHuyHieu()`; `traHua()` tự
         * chống gọi hai lần nên gọi lại ở đây là vô hại. */
        traHua();

        if (line && ruleMax > 0 && typeof line.quantity === 'number' && line.quantity > ruleMax) {
          notifyQtyRule(form, 'max', ruleMax);
          var capVid = line.variant_id != null ? String(line.variant_id) : String(snapshotId);
          /* 25/9/2026 (vòng 53, code-cart#2) — ĐỊA CHỈ GỬI ĐI LÀ LINE KEY, KHÔNG
           * PHẢI VARIANT ID. change.js {id:<variantId>} ĐẶT MỌI DÒNG mang biến thể
           * đó về cùng một số (đo LIVE 25/9: quà 1 + thường 5, thêm 1 vượt trần 5
           * ⇒ CẢ HAI dòng thành 5, giỏ 6 món → 10 món, $41,94 → $69,90). Dòng anh
           * em (quà có properties, dòng đăng ký giao định kỳ selling_plan) bị kéo
           * LÊN trần thay vì đứng yên. `line.key` có sẵn trong phản hồi add.js vừa
           * nhận, trên CÙNG hàng đợi nối tiếp, trỏ đúng MỘT dòng vừa gộp.
           * Tách hai vai y như 1168a88 (card-stepper): `capVid` vẫn là DANH TÍNH
           * cho `fixedLine.variant_id` (card-stepper đọc nó làm khoá), còn
           * `capDiaChi` chỉ là địa chỉ của lượt ghi.
           * ⛔ CỐ Ý KHÔNG bắn lại theo variantId khi key bị từ chối — đó là "cửa
           * diện rộng" mở lại chính con bug này (card-stepper.js đã đóng cùng lý
           * do). Từ chối thì nhánh "bad cart response shape" dưới đây vẫn phát
           * cart:updated và lượt hoà giải /cart.js chốt lại số thật.
           * Người gác: scripts/test-sua-tran-them-hang-gui-dia-chi-dong.js */
          var capDiaChi = line.key ? String(line.key) : capVid;
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: capDiaChi, quantity: ruleMax })
          })
            .then(function (r) { return r.json().catch(function () { return null; }); })
            .then(function (cart) {
              var fixedLine = { variant_id: capVid, quantity: ruleMax };
              if (cart && typeof cart.item_count === 'number') {
                var reliableTotal =
                  typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price :
                  typeof cart.original_total_price === 'number' ? cart.original_total_price :
                  cart.total_price;
                if (typeof reliableTotal === 'number' && reliableTotal >= 0) {
                  dispatchCartUpdated(fixedLine, cart.item_count, reliableTotal, cart);
                } else {
                  if (window.console && console.warn) console.warn('[aef-add-queue] cap-correct: bad total in cart response, syncing count only', cart);
                  dispatchCartUpdated(fixedLine, cart.item_count, undefined, cart);
                }
              } else {
                if (window.console && console.warn) console.warn('[aef-add-queue] cap-correct: bad cart response shape', cart);
                dispatchCartUpdated(line);
              }
              return settle(SETTLE_MS);
            })
            .catch(function (err) {
              if (window.console && console.warn) console.warn('[aef-add-queue] cap-correct change failed', err);
              dispatchCartUpdated(line);
              return settle(SETTLE_MS);
            });
        }

        /* 🔴 6/9/2026 (vòng 43) — DÙNG LẠI CON SỐ ĐÃ TÍNH LÚC BẤM, ĐỪNG TÍNH LẠI.
         *
         * Bản cũ ở đây ĐỌC LẠI huy hiệu rồi cộng `qty` lần nữa. Điều đó đúng khi
         * lúc bấm KHÔNG ai ghi gì (huy hiệu còn là số trước-khi-thêm). Nay lúc bấm
         * đã ghi lạc quan `demCu + qty`, nên đọc lại rồi cộng tiếp là **CỘNG ĐÔI**:
         * thêm 1 món mà huy hiệu nhảy 2. Đúng khuôn "một khái niệm, hai nguồn" —
         * hai chỗ cùng tính một con số, mỗi chỗ đọc một đầu vào khác nhau.
         *
         * Chữa bằng cách bỏ hẳn nguồn thứ hai: mốc `demCu`/`tienCu` chụp một lần
         * lúc bấm là nguồn DUY NHẤT, cả lượt ghi lạc quan lẫn lượt chốt đều đọc nó. */
        /* 🔴 14/9/2026 (vòng 48) — MỐC `demCu` ĐÃ ÔI LÚC TỚI ĐÂY. Chú thích ngay
         * trên đúng ở chiều CỘNG ĐÔI, nhưng nó đóng băng con số từ lúc BẤM tới
         * lúc máy chủ TRẢ LỜI — mà giữa hai mốc đó các cú bấm sau đã hứa cao hơn.
         * ĐO ĐƯỢC (5 cú / 5 thẻ khác nhau / 180ms): `detail.count` của 5 lượt chốt
         * ra 1,2,2,2,3 trong khi máy chủ đi 1,2,3,4,5 ⇒ huy hiệu tụt ngược 5 lần
         * và toast chỉ nổ 3/5.
         *
         * Lượt ghi lạc quan lúc bấm ĐÃ cộng phần của mình rồi, nên việc duy nhất
         * còn lại ở đây là CHỐT LẠI ĐÚNG CÁI ĐANG HIỆN — đọc lại DOM, không cộng
         * thêm. Ghi lại cùng một số là no-op cho mắt khách, nhưng `detail.count`
         * vẫn là một con số thật và ĐƠN ĐIỆU TĂNG. Toast của aef-cart-drawer.js
         * CỐ Ý gộp cả tràng và đếm bằng `postCount − toastBaseCount` (:1033), nên
         * thứ phải đúng là CON SỐ nó kể: đo lại sau khi vá, toast kể ĐỦ 5/5 món
         * (trước khi vá chỉ kể 3/5). (Vì thế KHÔNG chọn cách "bỏ hẳn
         * lượt ghi thứ hai": bỏ đi thì detail không còn `count`, cổng
         * `typeof d.count === 'number'` rớt và MỌI lượt thêm hàng từ thẻ/PDP mất
         * lời xác nhận.)
         *
         * Nhánh `else`: khối popup không dựng được (không có #cart-icon-bubble)
         * nên chẳng ai ghi lạc quan — giữ nguyên đường tính cũ để đường thêm hàng
         * ở những trang đó không mất con số. */
        var newCount = null;
        var newTotal = null;
        if (line) {
          if (daGhiLacQuan) {
            newCount = docHuyHieu();
            if (typeof tienMoi === 'number') newTotal = docTienHeader();
          } else {
            newCount = demCu + qty;
            if (typeof tienMoi === 'number') newTotal = tienMoi;
          }
        }
        dispatchCartUpdated(line, newCount, newTotal, null, true);
        // BUG-7: no optimistic bump to roll back on failure. Badge stays at
        // server truth via the next syncFromCart / refreshFromServer cycle.
        // BUG-O mitigation: brief settle delay so Shopify's cart-cookie
        // session lock fully releases before the next request kicks off.
        return settle(SETTLE_MS);
      })
      .then(function () {
        bumpPending(btn, -1);
        // Thứ tự có chủ ý: gỡ trạng thái đang-gửi TRƯỚC rồi mới dán ✓. Ngược lại
        // thì `.is-added` và `.loading` chồng nhau một nhịp, mà cả hai đều dùng
        // `!important` cho width/padding — đúng kiểu đua ghi đã đẻ ra BUG-7.
        if (addOk) flashAdded(btn);
        window.__aefCartAddPending =
          Math.max(0, (window.__aefCartAddPending || 1) - 1);
      });
  }

  // Capture-phase click listener — hijack BEFORE product-form's submit
  // handler runs. Resolves the <product-form> host even when the button
  // is rendered outside it via HTML5 form="..." (card__quick-add pattern).
  document.addEventListener('click', function (e) {
    try {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[type="submit"]');
      if (!btn) return;
      var form = resolveForm(btn);
      if (!form) return;
      // Only hijack buttons whose host form is a product-form (or contains
      // an `id` input pointing at a variant). Skip non-cart submit buttons
      // (search forms, footer signup, etc.).
      var isProductForm =
        form.tagName === 'PRODUCT-FORM' ||
        (form.closest && form.closest('product-form')) ||
        form.querySelector('input[name="id"][value]');
      if (!isProductForm) return;
      var variantInput = form.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) return;

      // Hijack — prevent default submit; product-form's submit listener
      // never fires because the form submission is canceled at click time.
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      /* 2026-08-17 — CHỐT CHỐNG BẤM HAI LẦN.
       *
       * `pendingPerBtn` có từ trước nhưng chỉ dùng để ĐẾM rồi bật/tắt hình
       * loading; đếm xong vẫn cho chạy tiếp. Và `setLoading` chỉ đặt
       * `aria-disabled` — thuộc tính đó KHÔNG chặn click, nó chỉ nói cho trình
       * đọc màn hình. Nên hai cú bấm liên tiếp đều lọt xuống queueAdd.
       *
       * Hàng đợi chạy NỐI TIẾP nên không mất request nào — cả hai lần đều
       * thành công. Đây là thêm đôi THẬT, không phải nhấp nháy giao diện. Với
       * 955 SP có `custom.min_qty` (vd min 3) thì bấm nhầm hai cái = khách nhận 6.
       *
       * Vì sao chặn là ĐÚNG ở đây, chứ không phải "cứ cho thêm 2 vì khách muốn 2":
       *   · thẻ SP — sau lần thêm đầu, card-stepper thay nút "+" bằng ô +/−;
       *     muốn 5 món thì đường đúng là cái stepper đó, không phải bấm "+" 5 lần.
       *   · PDP — đã có ô số lượng ngay trên nút.
       *   · đây cũng đúng mẫu đang chạy ở Market Deals (aef-marketdeals.js:128).
       *
       * ⚠ CHỈ chặn ở đường CLICK. `window.aefCartAdd()` và hàng đợi
       * `__aefCartAddQueue` giữ nguyên hợp đồng cũ — bộ giỏ có ca "rapid 5×:
       * server item_count=6" gọi thẳng API đó, và ca ấy phải tiếp tục xanh. */
      if ((pendingPerBtn.get(btn) || 0) > 0) return;

      queueAdd(form, btn);
      // ticket_BUG_fly_to_cart_no_motion (2026-06-11): đường add CHÍNH (card "+"
      // quick-add + PDP Add To Cart) trước giờ không hề gọi animation — chỉ
      // restore-from-Saved trong drawer có. Fire tại đây, một lần mỗi click,
      // optimistic như aefCartAdd. aefFlyToCart tự tìm ảnh sản phẩm gần nhất
      // (.wbproduct-container / PDP gallery) + tự fallback bounce badge.
      if (window.aefFlyToCart) { try { window.aefFlyToCart(btn); } catch (flyErr) {} }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[aef-add-queue] click handler error:', err);
      }
    }
  }, true);
})();
